import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  type LoggedEvent,
  type MeResponse,
  type Summary,
  type EventInput,
} from '../api';
import { messageFor } from '../App';
import { EventForm } from './EventForm';

/** Reviewing and correcting the log, with progress against the statute above it. */
export function LogView({ me, onChanged }: { me: MeResponse; onChanged: () => void }) {
  const [range, setRange] = useState(() => defaultRange(me));
  const [childId, setChildId] = useState('');
  const [events, setEvents] = useState<LoggedEvent[]>([]);
  const [summary, setSummary] = useState<Summary>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string>();
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { ...range, ...(childId ? { childId } : {}) };
      const [log, totals] = await Promise.all([api.events(params), api.summary(params)]);
      setEvents(log.events);
      setSummary(totals);
      setError(undefined);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }, [range, childId]);

  useEffect(() => {
    void load();
  }, [load]);

  const kids = me.children ?? [];
  const subjects = me.subjects ?? [];
  const nameById = useMemo(() => new Map(kids.map((c) => [c.childId, c.name])), [kids]);

  return (
    <>
      <div className="panel">
        <div className="row">
          <div>
            <label htmlFor="from">From</label>
            <input
              id="from"
              type="date"
              value={range.from}
              onChange={(e) => setRange({ ...range, from: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="to">To</label>
            <input
              id="to"
              type="date"
              value={range.to}
              onChange={(e) => setRange({ ...range, to: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="child">Child</label>
            <select id="child" value={childId} onChange={(e) => setChildId(e.target.value)}>
              <option value="">All children</option>
              {kids.map((child) => (
                <option key={child.childId} value={child.childId}>{child.name}</option>
              ))}
            </select>
          </div>
          <div className="narrow">
            <button onClick={() => setRange(schoolYear(me))}>School year</button>
          </div>
        </div>
      </div>

      {error && <div className="notice error">{error}</div>}

      {summary && summary.byChild.length > 0 && (
        <div className="panel">
          <h2>Progress in this range</h2>
          <p className="sub">
            {summary.eventCount} entries
            {summary.unverifiedCount > 0 && ` · ${summary.unverifiedCount} awaiting confirmation`}
            {' · '}each student is measured against their own requirement
          </p>

          {summary.byChild.map((child) => (
            <div key={child.childId} style={{ marginBottom: 18 }}>
              <div className="label" style={{ marginBottom: 6 }}>
                <span>
                  <strong>{child.name}</strong>{' '}
                  <span className="muted">
                    {child.jurisdiction.label}
                    {child.jurisdiction.citation !== 'n/a' && ` · ${child.jurisdiction.citation}`}
                  </span>
                </span>
                <span className="muted">
                  {(child.totals.totalMinutes / 60).toFixed(2)} hrs logged
                </span>
              </div>

              {child.progress.length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>
                  No hour requirement configured, so nothing is measured against a target.
                </p>
              ) : (
                <div className="progress">
                  {child.progress.map((prong) => (
                    <div key={prong.label} className={prong.satisfied ? 'met' : ''}>
                      <div className="label">
                        <span>{prong.label}</span>
                        <span className="muted">
                          {prong.loggedHours.toFixed(1)} / {prong.requiredHours} hrs
                          {prong.satisfied
                            ? ' · met'
                            : ` · ${prong.remainingHours.toFixed(1)} to go`}
                        </span>
                      </div>
                      <div className="bar">
                        <span
                          style={{
                            width: `${Math.min(100, (prong.loggedHours / prong.requiredHours) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="panel">
        <h2>Entries</h2>
        <p className="sub">
          Highlighted rows were drafted from a recording or by the assistant and have not been
          confirmed by a parent yet.
        </p>

        {busy && <p className="muted">Loading…</p>}

        {!busy && events.length === 0 && (
          <p className="muted">Nothing logged in this range.</p>
        )}

        {events.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Time</th><th>Hrs</th><th>Subject</th>
                <th>Where</th><th>Who</th><th>What was covered</th><th />
              </tr>
            </thead>
            <tbody>
              {events.map((event) =>
                editing === event.eventId ? (
                  <tr key={event.eventId}>
                    <td colSpan={8}>
                      <EventForm
                        subjects={subjects}
                        kids={kids}
                        initial={event}
                        onCancel={() => setEditing(undefined)}
                        onSave={async (changes) => {
                          await api.updateEvent(event.date, event.eventId, {
                            changes,
                            expectedRevision: event.revision,
                            reason: 'Edited in the web app',
                          });
                          setEditing(undefined);
                          await load();
                        }}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={event.eventId} className={event.verified ? '' : 'unverified'}>
                    <td>{event.date}</td>
                    <td className="muted">{event.startTime}–{event.endTime}</td>
                    <td>{(event.durationMinutes / 60).toFixed(2)}</td>
                    <td>
                      <span className={subjects.find((s) => s.id === event.subject)?.core ? 'pill core' : 'pill'}>
                        {subjects.find((s) => s.id === event.subject)?.label ?? event.subject}
                      </span>
                    </td>
                    <td className="muted">{event.location === 'home' ? 'Home' : 'Away'}</td>
                    <td className="muted">
                      {event.childIds.map((id) => nameById.get(id) ?? '?').join(', ')}
                    </td>
                    <td>{event.description}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {!event.verified && (
                        <button
                          onClick={async () => {
                            try {
                              await api.verifyEvent(event.date, event.eventId, event.revision);
                              await load();
                            } catch (err) {
                              setError(messageFor(err));
                            }
                          }}
                        >
                          Confirm
                        </button>
                      )}{' '}
                      <button onClick={() => setEditing(event.eventId)}>Edit</button>{' '}
                      <button
                        className="danger"
                        onClick={async () => {
                          if (!window.confirm('Delete this entry? It stays on the audit trail.')) {
                            return;
                          }
                          try {
                            await api.deleteEvent(event.date, event.eventId, 'Deleted from the log');
                            await load();
                            onChanged();
                          } catch (err) {
                            setError(messageFor(err));
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}

        <div style={{ marginTop: 16 }}>
          {adding ? (
            <EventForm
              subjects={subjects}
              kids={kids}
              onCancel={() => setAdding(false)}
              onSave={async (input) => {
                await api.createEvent(input as EventInput);
                setAdding(false);
                await load();
                onChanged();
              }}
            />
          ) : (
            <button onClick={() => setAdding(true)} disabled={kids.length === 0}>
              Add an entry by hand
            </button>
          )}
          {kids.length === 0 && (
            <p className="muted">Add a child in Settings before logging anything.</p>
          )}
        </div>
      </div>
    </>
  );
}

function durationMinutes(start: string, end: string): number | undefined {
  const parse = (value: string): number | undefined => {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
    return match ? Number(match[1]) * 60 + Number(match[2]) : undefined;
  };
  const a = parse(start);
  const b = parse(end);
  if (a === undefined || b === undefined || b <= a) return undefined;
  return b - a;
}

function defaultRange(me: MeResponse): { from: string; to: string } {
  const today = localToday(me);
  const from = new Date(`${today}T00:00:00Z`);
  from.setUTCDate(from.getUTCDate() - 13);
  return { from: from.toISOString().slice(0, 10), to: today };
}

/** The school year to date, which is the window that matters for the hour requirement. */
function schoolYear(me: MeResponse): { from: string; to: string } {
  const today = localToday(me);
  const year = Number(today.slice(0, 4));
  const start = `${year}-07-01`;
  return { from: today >= start ? start : `${year - 1}-07-01`, to: today };
}

function localToday(me: MeResponse): string {
  const zone = me.account?.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
